import { useState, useEffect } from "react";
import { Box, Drawer, Typography, Chip, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ListAltIcon from "@mui/icons-material/ListAlt";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import BoltIcon from "@mui/icons-material/Bolt";
import { useTranslation } from "react-i18next";
import { resolveLabel } from "../utils/labelUtils";

const GAUGE_DELTA_CONFIG = [
  { key: "de_logique",     labelKey: "gauges.logique" },
  { key: "de_social",     labelKey: "gauges.social" },
  { key: "de_sensoriel",   labelKey: "gauges.sensoriel" },
  { key: "de_passion",     labelKey: "gauges.passion" },
  { key: "de_motivation", labelKey: "gauges.motivation" },
  { key: "de_reserve",   labelKey: "gauges.reserve" },
  { key: "de_stamina",     labelKey: "gauges.stamina" },
];

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const EventDeltaChips = ({ event, t }) => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4, mt: 0.4 }}>
    {GAUGE_DELTA_CONFIG.map(({ key, labelKey }) => {
      const delta = event[key];
      if (!delta) return null;
      return (
        <Chip
          key={key}
          label={`${t(labelKey)} ${delta > 0 ? "+" : ""}${delta}`}
          size="small"
          sx={{
            fontSize: "0.65rem",
            height: 18,
            backgroundColor: delta > 0 ? "#166534" : "#7f1d1d",
            color: delta > 0 ? "#4ade80" : "#f87171",
          }}
        />
      );
    })}
  </Box>
);

export default function DayEventsPanel({ refreshTrigger }) {
  const { t } = useTranslation();
  const lang = document.documentElement.lang === "en" ? "en" : "fr";

  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchEvents = () => {
    setLoading(true);
    fetch("/api/day-events", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setEvents)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) fetchEvents();
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (open) fetchEvents();
  }, [refreshTrigger]); // eslint-disable-line

  return (
    <>
      {/* Onglet permanent sur le bord gauche */}
      <Box
        onClick={() => setOpen(true)}
        sx={{
          position: "fixed",
          left: 0,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1200,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          py: 1.25,
          px: 0.75,
          backgroundColor: "#0f172a",
          border: "1px solid #1e293b",
          borderLeft: "none",
          borderRadius: "0 8px 8px 0",
          color: "#93c5fd",
          cursor: "pointer",
          boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
          "&:hover": { backgroundColor: "#1e293b" },
        }}
      >
        <ListAltIcon fontSize="small" />
        <Typography
          sx={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            fontSize: "0.68rem",
            letterSpacing: "0.03em",
          }}
        >
          {t("dayEvents.tabLabel")}
        </Typography>
      </Box>

      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#0f172a", color: "#e2e8f0", width: 320, maxWidth: "88vw" } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2, pb: 1 }}>
          <Typography sx={{ fontWeight: 700 }}>{t("dayEvents.title")}</Typography>
          <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: "#64748b" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ px: 2, pb: 2, overflowY: "auto" }}>
          {!loading && events.length === 0 && (
            <Typography sx={{ color: "#475569", fontSize: "0.85rem", fontStyle: "italic", mt: 2 }}>
              {t("dayEvents.empty")}
            </Typography>
          )}

          {events.map((event) => (
            <Box
              key={event.de_ID}
              sx={{
                display: "flex",
                gap: 1,
                py: 1,
                borderBottom: "1px solid #1e293b",
              }}
            >
              <Box sx={{ color: event.de_type === "morning_start" ? "#86efac" : "#93c5fd", mt: 0.25 }}>
                {event.de_type === "morning_start" ? (
                  <WbSunnyIcon fontSize="small" />
                ) : (
                  <BoltIcon fontSize="small" />
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.82rem", color: "#e2e8f0" }}>
                    {resolveLabel(event, "de_label", lang) || t(`dayEvents.type.${event.de_type}`)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "#64748b", flexShrink: 0 }}>
                    {fmtTime(event.de_created_at)}
                  </Typography>
                </Box>
                <EventDeltaChips event={event} t={t} />
              </Box>
            </Box>
          ))}
        </Box>
      </Drawer>
    </>
  );
}
